import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Headers from "../Header";
import Footer from "../Footer";
import { LayoutGrid, Eye, EyeOff, Loader2 } from "lucide-react";
import AssessImage from "../../assets/Assessment.jpg";
import config from "../../config/config";

const API_URL = config.API_URL;

const CompanyRegister = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Phone number regex
  const phoneRex = /^\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/;

  // Yup validation schema
  const validationSchema = Yup.object({
    adminName: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters"),
    
    phone: Yup.string()
      .required("Phone Number is required")
      .matches(phoneRex, "Enter a Valid Phone Number"),
    
    adminEmail: Yup.string()
      .required("Email is required")
      .email("Enter a valid email address"),
    
    adminPassword: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    
    companyName: Yup.string()
      .required("Company Name is required")
      .min(2, "Company name must be at least 2 characters")
      .max(100, "Company name must be less than 100 characters"),
    
    companyUrl: Yup.string()
      .required("Company Url is required")
      .url("Enter a valid URL (e.g., https://example.com)"),
    
    address: Yup.string()
      .required("Company Address is required")
      .min(10, "Address must be at least 10 characters")
      .max(200, "Address must be less than 200 characters")
  });

  // Initial form values
  const initialValues = {
    adminName: "",
    phone: "",
    adminEmail: "",
    adminPassword: "",
    companyName: "",
    companyUrl: "",
    address: "",
  };

  // Form submission handler
  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/company/register`, values, {
        headers: {
          'Content-Type': 'application/json',
          // other headers if required
        }
      });
      toast.success("Company registered successfully!");
      setIsLoading(false);
      navigate("/login", { state: { registrationSuccess: true } });
    } catch (error) {
      console.error("Registration error:", error);
      
      // Handle specific field errors from server
      if (error.response?.data?.fieldErrors) {
        Object.keys(error.response.data.fieldErrors).forEach(field => {
          setFieldError(field, error.response.data.fieldErrors[field]);
        });
      } else {
        toast.error(
          error.response?.data?.message || "Registration failed. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div>
        <Headers />
      </div>
      <div className="mt-20 min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-7xl flex items-center justify-center">
        <Card className="w-full min-h-screen lg:min-h-fit flex flex-row overflow-hidden shadow-2xl">
          {/* Left side - Image Section (50% width) */}
          <div className="w-full lg:w-1/2 min-h-64 lg:min-h-full hidden lg:block bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-6">
            <div className="text-center w-full h-full flex items-center justify-center">
              <img 
                src={AssessImage}
                alt="Assessment Image" 
                className="w-full h-full max-h-96 lg:max-h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
          
          {/* Right side - Form Section (50% width) */}
          <div className="w-full lg:w-1/2 flex flex-col">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-3xl font-bold">Register Your Company</CardTitle>
              <CardDescription>
                Create a company account to start managing assessments
              </CardDescription>
            </CardHeader>
            
                      <CardContent>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form className="space-y-4">
                  <p className="font-bold text-lg">Personal Details</p>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="adminName">Name</Label>
                      <Field
                        as={Input}
                        id="adminName"
                        name="adminName"
                        placeholder="John Doe"
                        value={values.adminName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.adminName && touched.adminName ? "border-red-500 focus:border-red-500" : ""}
                      />
                      <ErrorMessage name="adminName" component="p" className="text-sm text-red-500" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Field
                        as={Input}
                        id="phone"
                        name="phone"
                        placeholder="+1 234 567 8901"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.phone && touched.phone ? "border-red-500 focus:border-red-500" : ""}
                      />
                      <ErrorMessage name="phone" component="p" className="text-sm text-red-500" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="adminEmail">Email</Label>
                      <Field
                        as={Input}
                        id="adminEmail"
                        name="adminEmail"
                        type="email"
                        placeholder="admin@example.com"
                        value={values.adminEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.adminEmail && touched.adminEmail ? "border-red-500 focus:border-red-500" : ""}
                      />
                      <ErrorMessage name="adminEmail" component="p" className="text-sm text-red-500" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="adminPassword">Password</Label>
                      <div className="relative">
                        <Input
                          as={Input}
                          id="adminPassword"
                          name="adminPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="********"
                          value={values.adminPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`pr-10 ${errors.adminPassword && touched.adminPassword ? "border-red-500 focus:border-red-500" : ""}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <ErrorMessage name="adminPassword" component="p" className="text-sm text-red-500" />
                    </div>
                  </div>

                  <p className="font-bold text-lg">Company Details</p>
                  
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Company Name</Label>
                      <Field
                        as={Input}
                        id="companyName"
                        name="companyName"
                        placeholder="ABC Corporation"
                        value={values.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.companyName && touched.companyName ? "border-red-500 focus:border-red-500" : ""}
                      />
                      <ErrorMessage name="companyName" component="p" className="text-sm text-red-500" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="companyUrl">Company Website</Label>
                      <Field
                        as={Input}
                        id="companyUrl"
                        name="companyUrl"
                        placeholder="https://example.com"
                        value={values.companyUrl}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.companyUrl && touched.companyUrl ? "border-red-500 focus:border-red-500" : ""}
                      />
                      <ErrorMessage name="companyUrl" component="p" className="text-sm text-red-500" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address">Company Address</Label>
                      <Field
                        as={Input}
                        id="address"
                        name="address"
                        placeholder="123 Main St, City, Country"
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={errors.address && touched.address ? "border-red-500 focus:border-red-500" : ""}
                      />
                      <ErrorMessage name="address" component="p" className="text-sm text-red-500" />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading || isSubmitting}
                  >{isLoading && <Loader2 className="animate-spin"/>}
                    {isLoading ? "Registering..." : "Register Company"}
                  </Button>
                </Form>
              )}
            </Formik>

            <div className="mt-4 text-center text-sm">
              Already have an account?{" "}
              <Button
                variant="link"
                className="p-0"
                onClick={() => navigate("/login")}
              >
                Login
              </Button>
            </div>
          </CardContent>
          </div>
        </Card>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default CompanyRegister;

